import { Box } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';
import GoogleIcon from './GoogleIcon';
import TikTokIcon from './TikTokIcon';

const BLUE = '#2F6BFF';
const DEEP_BLUE = '#0D2ED3';

// A field of dots, like the reference artwork's dotted grids
const dots = (color) => ({
  backgroundImage: `radial-gradient(${color} 1.6px, transparent 1.8px)`,
  backgroundSize: '26px 26px',
});

/**
 * The sign in page's background, after the TrialX Social artwork: a pale blue wash with a white glow on the left,
 * blue diagonal bands in the corners, a soft circle and a thin line bottom left, and two dotted grids. Purely
 * decorative; sits behind the page's content.
 */
export default function LoginBackdrop() {
  return (
    <Box aria-hidden sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none',
      background: 'linear-gradient(120deg, #F5F8FF 0%, #EDF3FF 45%, #DCE8FF 100%)' }}>
      {/* white glow behind the brand */}
      <Box sx={{ position: 'absolute', inset: 0,
        background: 'radial-gradient(circle at 22% 38%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 45%)' }} />

      {/* top right: a band, then the darker corner */}
      <Box sx={{ position: 'absolute', inset: 0, clipPath: 'polygon(70% 0, 84% 0, 100% 28%, 100% 54%)',
        background: `linear-gradient(160deg, ${alpha(BLUE, 0.08)} 0%, ${alpha(BLUE, 0.22)} 100%)` }} />
      <Box sx={{ position: 'absolute', inset: 0, clipPath: 'polygon(86% 0, 100% 0, 100% 24%)',
        background: `linear-gradient(200deg, ${alpha(BLUE, 0.75)} 0%, ${alpha(BLUE, 0.25)} 100%)` }} />

      {/* bottom right corner */}
      <Box sx={{ position: 'absolute', inset: 0, clipPath: 'polygon(100% 58%, 100% 100%, 74% 100%)',
        background: `linear-gradient(315deg, ${alpha(BLUE, 0.55)} 0%, ${alpha(BLUE, 0.1)} 100%)` }} />
      <Box sx={{ position: 'absolute', inset: 0, clipPath: 'polygon(100% 40%, 100% 52%, 66% 100%, 58% 100%)',
        background: alpha(BLUE, 0.08) }} />

      {/* bottom left: a soft circle, a light corner and a thin line */}
      <Box sx={{ position: 'absolute', width: 560, height: 560, borderRadius: '50%', left: -220, bottom: -260,
        background: `radial-gradient(circle, ${alpha(BLUE, 0.1)} 0%, ${alpha(BLUE, 0.04)} 70%)` }} />
      <Box sx={{ position: 'absolute', inset: 0, clipPath: 'polygon(0 72%, 0 100%, 16% 100%)',
        background: `linear-gradient(45deg, ${alpha(BLUE, 0.28)} 0%, ${alpha(BLUE, 0.04)} 100%)` }} />
      <Box sx={{ position: 'absolute', left: -40, bottom: '14%', width: 200, height: 2, bgcolor: DEEP_BLUE,
        opacity: 0.7, transform: 'rotate(45deg)', transformOrigin: 'left center' }} />

      {/* dotted grids */}
      <Box sx={{ position: 'absolute', top: '10%', left: '64%', width: 190, height: 70, opacity: 0.8,
        ...dots(alpha(BLUE, 0.3)) }} />
      <Box sx={{ position: 'absolute', bottom: '12%', left: '24%', width: 190, height: 70, opacity: 0.8,
        display: { xs: 'none', md: 'block' }, ...dots(alpha(BLUE, 0.3)) }} />
    </Box>
  );
}

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(var(--tilt)); }
  50% { transform: translateY(-8px) rotate(var(--tilt)); }
`;

const PLATFORMS = [
  { name: 'Facebook', Icon: FacebookIcon, bg: '#1877F2', color: '#fff', at: { left: -36, top: -76 }, tilt: '-6deg' },
  { name: 'X', Icon: XIcon, bg: '#111111', color: '#fff', at: { left: -118, top: '38%' }, tilt: '-10deg' },
  { name: 'YouTube', Icon: YouTubeIcon, bg: '#FF0033', color: '#fff', at: { left: -72, bottom: -24 }, tilt: '8deg' },
  { name: 'Instagram', Icon: InstagramIcon, color: '#fff', at: { right: 48, top: -98 }, tilt: '6deg',
    bg: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fd5949 45%, #d6249f 60%, #285AEB 90%)' },
  { name: 'Google', Icon: GoogleIcon, bg: '#ffffff', at: { right: -84, top: -18 }, tilt: '-4deg', size: 32 },
  { name: 'TikTok', Icon: TikTokIcon, bg: '#111111', color: '#fff', at: { right: -104, top: '46%' }, tilt: '8deg' },
  { name: 'LinkedIn', Icon: LinkedInIcon, bg: '#0A66C2', color: '#fff', at: { right: 36, bottom: -72 }, tilt: '-6deg' },
];

/**
 * The orbit around the sign in card on wide screens: two thin arcs and the platforms' tiles, gently floating
 * (still when the browser asks for reduced motion)
 */
export function PlatformOrbit() {
  return (
    <Box aria-hidden sx={{ display: { xs: 'none', lg: 'block' }, position: 'absolute', inset: 0,
      pointerEvents: 'none' }}>
      {[760, 1040].map((size) => (
        <Box key={size} sx={{ position: 'absolute', width: size, height: size, left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)', borderRadius: '50%', border: `1.5px solid ${alpha(BLUE, 0.16)}`,
          zIndex: -1 }} />
      ))}
      {PLATFORMS.map(({ name, Icon, bg, color, at, tilt, size = 36 }, index) => (
        <Box key={name} sx={{ position: 'absolute', ...at, width: 64, height: 64, borderRadius: '18px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, color,
          boxShadow: '0 14px 30px rgba(13, 46, 211, 0.18)', '--tilt': tilt, transform: `rotate(${tilt})`,
          animation: `${float} ${5 + index * 0.6}s ease-in-out ${index * 0.4}s infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
          <Icon sx={{ fontSize: size }} />
        </Box>
      ))}
    </Box>
  );
}
